import React, { useState, useEffect } from 'react';
import { X, Star, Gift, Heart, Send, Loader2, Check, Copy } from 'lucide-react';
import { Heading, BodyText, SmallText } from './brand/BrandTypography';
import { PrimaryButton, SecondaryButton, TertiaryButton } from './brand/BrandButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialPrompt = ({ isOpen, onClose, userTier = 'free' }) => {
    const [step, setStep] = useState('rating'); // 'rating' | 'review' | 'thanks' | 'feedback'
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [reviewText, setReviewText] = useState('');
    const [allowPublish, setAllowPublish] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [discountCode, setDiscountCode] = useState(null);
    const [validTiers, setValidTiers] = useState([]);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState(null);

    // Get reward description based on tier
    const getRewardDescription = () => {
        if (userTier === 'free') {
            return "Get 40% off your first month of Essentials or Support+ tier!";
        }
        return "Get 40% off your next month's subscription!";
    };

    const handleRatingSelect = (selectedRating) => {
        setRating(selectedRating);
        if (selectedRating >= 4) {
            setStep('review');
        } else {
            setStep('feedback');
        }
    };

    const handleSubmit = async () => {
        if (reviewText.length < 10) {
            setError('Please write at least 10 characters');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/testimonials/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    rating,
                    text: reviewText,
                    allow_publish: allowPublish
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Failed to submit testimonial');
            }

            if (data.discount_code) {
                setDiscountCode(data.discount_code);
                setValidTiers(data.valid_tiers || []);
            }
            setStep('thanks');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDismiss = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            await fetch(`${API_URL}/api/testimonials/dismiss`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
        } catch (err) {
            console.error('Failed to dismiss:', err);
        }
        onClose();
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(discountCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getTierLabel = (tier) => {
        const labels = {
            'essentials': 'Essentials',
            'support_plus': 'Support+',
            'partner': 'Partner'
        };
        return labels[tier] || tier;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={handleDismiss}
            />
            
            {/* Modal */}
            <div 
                className="relative w-full max-w-md bg-[#FEF6EF] rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
                data-testid="testimonial-modal"
            >
                {/* Close button */}
                <button
                    onClick={handleDismiss}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#FFBB98]/20 transition-colors z-10"
                    data-testid="testimonial-close-btn"
                >
                    <X className="w-5 h-5 text-[#7D8E95]" />
                </button>

                {/* Content */}
                <div className="p-8">
                    {/* Step: Rating */}
                    {step === 'rating' && (
                        <div className="text-center" data-testid="testimonial-rating-step">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center">
                                <Heart className="w-8 h-8 text-white" />
                            </div>
                            
                            <Heading as="h2" size="text-xl" className="text-[#1D6B6B] mb-2">
                                How's your experience?
                            </Heading>
                            <BodyText className="text-[#7D8E95] mb-8">
                                You've been with us for a week now. We'd love to hear how CoRegulate is working for you.
                            </BodyText>

                            {/* Star rating */}
                            <div className="flex justify-center gap-2 mb-8">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        onClick={() => handleRatingSelect(star)}
                                        onMouseEnter={() => setHoverRating(star)}
                                        onMouseLeave={() => setHoverRating(0)}
                                        className="p-1 transition-transform hover:scale-110"
                                        data-testid={`rating-star-${star}`}
                                    >
                                        <Star 
                                            className={`w-10 h-10 transition-colors ${
                                                star <= (hoverRating || rating)
                                                    ? 'text-[#E4D3A3] fill-[#E4D3A3]'
                                                    : 'text-[#E4D3A3]/30'
                                            }`}
                                        />
                                    </button>
                                ))}
                            </div>

                            {/* Reward hint */}
                            <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#1D6B6B]/5 border border-[#1D6B6B]/10">
                                <Gift className="w-5 h-5 text-[#1D6B6B]" />
                                <SmallText className="text-[#1D6B6B]">
                                    {getRewardDescription()}
                                </SmallText>
                            </div>

                            <button
                                onClick={handleDismiss}
                                className="mt-6 text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                            >
                                Maybe later
                            </button>
                        </div>
                    )}

                    {/* Step: Review (positive) */}
                    {step === 'review' && (
                        <div data-testid="testimonial-review-step">
                            <div className="text-center mb-6">
                                <div className="flex justify-center gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star 
                                            key={star}
                                            className={`w-6 h-6 ${
                                                star <= rating
                                                    ? 'text-[#E4D3A3] fill-[#E4D3A3]'
                                                    : 'text-[#E4D3A3]/30'
                                            }`}
                                        />
                                    ))}
                                </div>
                                <Heading as="h2" size="text-xl" className="text-[#1D6B6B] mb-2">
                                    We're so glad! 💚
                                </Heading>
                                <BodyText className="text-[#7D8E95]">
                                    Your words help others find the support they need.
                                </BodyText>
                            </div>

                            <textarea
                                value={reviewText}
                                onChange={(e) => setReviewText(e.target.value)}
                                placeholder="What's been most helpful for you? How has CoRegulate supported your emotional regulation?"
                                className="w-full h-32 p-4 rounded-xl bg-white border border-[#FFBB98]/30 text-[#344648] placeholder-[#7D8E95]/60 resize-none focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/20 focus:border-[#1D6B6B]/30"
                                data-testid="testimonial-text-input"
                            />
                            
                            <div className="flex items-start gap-3 mt-4 mb-6">
                                <input
                                    type="checkbox"
                                    id="allowPublish"
                                    checked={allowPublish}
                                    onChange={(e) => setAllowPublish(e.target.checked)}
                                    className="mt-1 w-4 h-4 rounded border-[#FFBB98]/50 text-[#1D6B6B] focus:ring-[#1D6B6B]/20"
                                />
                                <label htmlFor="allowPublish" className="text-sm text-[#7D8E95]">
                                    I'm happy for my review to be shared on the CoRegulate website (first name only)
                                </label>
                            </div>

                            {error && (
                                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200">
                                    <SmallText className="text-red-600">{error}</SmallText>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <SecondaryButton
                                    onClick={() => setStep('rating')}
                                    className="flex-1"
                                >
                                    Back
                                </SecondaryButton>
                                <PrimaryButton
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || reviewText.length < 10}
                                    className="flex-1"
                                    data-testid="testimonial-submit-btn"
                                >
                                    {isSubmitting ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4 mr-2" />
                                            Submit
                                        </>
                                    )}
                                </PrimaryButton>
                            </div>
                        </div>
                    )}

                    {/* Step: Feedback (lower rating) */}
                    {step === 'feedback' && (
                        <div data-testid="testimonial-feedback-step">
                            <div className="text-center mb-6">
                                <div className="flex justify-center gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star 
                                            key={star}
                                            className={`w-6 h-6 ${
                                                star <= rating
                                                    ? 'text-[#E4D3A3] fill-[#E4D3A3]'
                                                    : 'text-[#E4D3A3]/30'
                                            }`}
                                        />
                                    ))}
                                </div>
                                <Heading as="h2" size="text-xl" className="text-[#1D6B6B] mb-2">
                                    Thank you for being honest
                                </Heading>
                                <BodyText className="text-[#7D8E95]">
                                    Your feedback helps us improve. What could we do better?
                                </BodyText>
                            </div>

                            <textarea
                                value={reviewText}
                                onChange={(e) => setReviewText(e.target.value)}
                                placeholder="What's not working for you? What would make CoRegulate more helpful?"
                                className="w-full h-32 p-4 rounded-xl bg-white border border-[#FFBB98]/30 text-[#344648] placeholder-[#7D8E95]/60 resize-none focus:outline-none focus:ring-2 focus:ring-[#1D6B6B]/20 focus:border-[#1D6B6B]/30"
                                data-testid="feedback-text-input"
                            />

                            {error && (
                                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200">
                                    <SmallText className="text-red-600">{error}</SmallText>
                                </div>
                            )}

                            <div className="flex gap-3 mt-6">
                                <SecondaryButton
                                    onClick={() => setStep('rating')}
                                    className="flex-1"
                                >
                                    Back
                                </SecondaryButton>
                                <PrimaryButton
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || reviewText.length < 10}
                                    className="flex-1"
                                    data-testid="feedback-submit-btn"
                                >
                                    {isSubmitting ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4 mr-2" />
                                            Send Feedback
                                        </>
                                    )}
                                </PrimaryButton>
                            </div>
                        </div>
                    )}

                    {/* Step: Thanks */}
                    {step === 'thanks' && (
                        <div className="text-center" data-testid="testimonial-thanks-step">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center">
                                <Check className="w-8 h-8 text-white" />
                            </div>
                            
                            <Heading as="h2" size="text-xl" className="text-[#1D6B6B] mb-2">
                                Thank you! 💚
                            </Heading>
                            <BodyText className="text-[#7D8E95] mb-6">
                                {rating >= 4 
                                    ? "Your kind words mean the world to us."
                                    : "We appreciate your honest feedback and will use it to improve."
                                }
                            </BodyText>

                            {/* Discount code (only for positive reviews) */}
                            {discountCode && (
                                <div className="mb-6 p-6 rounded-2xl bg-gradient-to-br from-[#1D6B6B]/10 to-[#145555]/5 border border-[#1D6B6B]/20">
                                    <div className="flex items-center justify-center gap-2 mb-3">
                                        <Gift className="w-5 h-5 text-[#1D6B6B]" />
                                        <SmallText className="font-semibold text-[#1D6B6B]">
                                            Your 40% Discount Code
                                        </SmallText>
                                    </div>
                                    
                                    <div className="flex items-center justify-center gap-2 mb-3">
                                        <code className="px-4 py-2 rounded-lg bg-white text-lg font-mono font-bold text-[#1D6B6B] border border-[#1D6B6B]/20">
                                            {discountCode}
                                        </code>
                                        <button
                                            onClick={copyToClipboard}
                                            className="p-2 rounded-lg hover:bg-white/50 transition-colors"
                                            data-testid="copy-discount-btn"
                                        >
                                            {copied ? (
                                                <Check className="w-5 h-5 text-green-600" />
                                            ) : (
                                                <Copy className="w-5 h-5 text-[#7D8E95]" />
                                            )}
                                        </button>
                                    </div>
                                    
                                    <SmallText className="text-[#7D8E95]">
                                        Valid for: {validTiers.map(getTierLabel).join(', ')}
                                    </SmallText>
                                    <SmallText className="text-[#7D8E95] block mt-1">
                                        Expires in 60 days
                                    </SmallText>
                                </div>
                            )}

                            <PrimaryButton
                                onClick={onClose}
                                className="w-full"
                                data-testid="testimonial-done-btn"
                            >
                                Continue to App
                            </PrimaryButton>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TestimonialPrompt;
